import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { validateString } from "./helpers/ObjectCheckerHelper";
import { API_CALL_NOTIFICATION } from "./constants/notification";
import { formatMessage, formatMessageWithParams, tryFormatMessageWithJson } from "./helpers/LanguageHelper";
import NotificationManager from "../common/notification/NotificationManager";
import LoadingManager from "../common/loading/LoadingManager";
import { handleSSOErrors } from "./helpers/SSOErrorsHandler";
import { RootState, store } from "../redux/store/store";
import {
  selectActiveConfiguration,
  selectBackendUrl,
  updateAuthDataForTenantConf,
} from "../redux/feature/settings/configurations-slice";
import { Buffer } from "buffer";
import { refreshToken } from "../common/login/RefreshToken";
import { AuthOutcome } from "../common/login/DialogCommons";
import { AxiosCustomParams } from "./helpers/axios.helpers";
import StandaloneModalManager from "../common/standaloneModal/StandaloneModalManager";

/* global Office, console */

const API_CALL_KEY = API_CALL_NOTIFICATION;

const defaultOptions: AxiosRequestConfig = {
  headers: {
    "Content-Type": "application/json",
  },
};
export const axiosInstance = axios.create(defaultOptions);

const azureAccessTokenWrapper: {
  token?: Promise<string>;
  acquiredTimestamp?: number;
} = {};

const bearerTokenLock: {
  lock?: Promise<AuthOutcome>;
} = {};

axiosInstance.interceptors.request.use(async function (config) {
  const state: RootState = store.getState();

  const params: AxiosCustomParams | undefined = config.params;

  if (!params || !params.notShowLoading) {
    LoadingManager.create(API_CALL_KEY + config.url);
  }
  const backend_url = selectBackendUrl(state);
  if (validateString(backend_url)) {
    config.baseURL = backend_url;
  }

  try {
    config.headers = { ...config.headers, ...(await getHeaders()) };
  } catch (e) {
    return Promise.reject(new AxiosError(e.message, e.code, config));
  }

  console.log("calling api " + config.url);

  return config;
});

export async function getHeaders(withMsToken = true): Promise<any> {
  const state = store.getState();
  const selectedConf = selectActiveConfiguration(state);
  const tenant_id = selectedConf.tenantId;

  const headers: any = {};

  if (validateString(tenant_id)) {
    headers["X-Tenant-Id"] = tenant_id;
  }

  let tokenIsPresent = false;
  let tokenIsValid = false;
  if (validateString(selectedConf?.authData?.access_token)) {
    tokenIsPresent = true;
    tokenIsValid = Date.now() < selectedConf.authData.expires_at * 1000;
  }

  if (tokenIsValid) {
    headers.Authorization = `Bearer ${selectedConf.authData.access_token}`;
  } else {
    // we haven't a valid access token we start login process
    console.log("token not valid we try to refresh");
    if (!bearerTokenLock.lock) {
      bearerTokenLock.lock = refreshToken(tenant_id);
    }
    try {
      const loginOutcome = await bearerTokenLock.lock;
      console.log("refresh token successful");
      headers.Authorization = `Bearer ${loginOutcome.authData.access_token}`;
    } catch (e) {
      console.error("error trying to refresh token", e);
      store.dispatch(updateAuthDataForTenantConf({ authData: undefined, tenantId: tenant_id }));
      throw new AuthError(tokenIsPresent ? e.toString() : "NO_TOKEN", "401");
    } finally {
      bearerTokenLock.lock = undefined;
    }
  }

  if (withMsToken) {
    try {
      if (
        !azureAccessTokenWrapper.acquiredTimestamp ||
        azureAccessTokenWrapper.acquiredTimestamp + 60000 < new Date().getTime()
      ) {
        console.log("trying to get office sso access token ...");
        azureAccessTokenWrapper.token = Office.auth.getAccessToken({
          allowSignInPrompt: true,
          allowConsentPrompt: true,
          forMSGraphAccess: true,
        });
        azureAccessTokenWrapper.acquiredTimestamp = new Date().getTime();
      }

      headers.Azure_AccessToken = await azureAccessTokenWrapper.token;
    } catch (e) {
      console.log("error trying to get token:" + e.message, e);
      handleSSOErrors(e);
    }
  }

  return headers;
}

axiosInstance.interceptors.response.use(
  (response) => {
    LoadingManager.remove(API_CALL_KEY + response.config.url);
    return response;
  },
  async (err) => {
    let errorTitle = "";
    let errorMessage: string;
    let severity = "error";
    let enableRefreshPanel: boolean;
    let notifyError: boolean = true;
    LoadingManager.remove(API_CALL_KEY + err.config.url);
    if (!err.response || err.response.data.length === 0) {
      console.error(err.message);
      const messageAndTitle = mapErrorToMessage(err);
      errorMessage = messageAndTitle.message;
      errorTitle = messageAndTitle.title;
    } else if (err.response.status === 501) {
      errorTitle = formatMessage("cof.axios.error.title.incompatibleversion");
      const currentVersion = err.response.data.currentVersion;
      const requiredVersion = err.response.data.requiredVersion;
      errorMessage = formatMessageWithParams("cof.axios.error.message.incompatibleversion", {
        currentVersion,
        requiredVersion,
      });
    } else if (err.response.status === 512) {
      /*Axios is trying to make an ArrayBuffer from the error object, so we need to convert it back to an object using TextDecoder and JSON.parse*/
      const decoder = new TextDecoder("utf-8");
      if (err.response.data instanceof ArrayBuffer) {
        severity = JSON.parse(decoder.decode(err.response.data))[0].severity;
        errorTitle = JSON.parse(decoder.decode(err.response.data))[0].error;
        errorMessage = JSON.parse(decoder.decode(err.response.data))[0].description;
      } else {
        const genericErrorsList = err.response.data;
        severity = genericErrorsList && genericErrorsList.length > 0 ? genericErrorsList[0].severity : "error";
        errorTitle =
          genericErrorsList && genericErrorsList.length > 0 ? tryFormatMessageWithJson(genericErrorsList[0].error) : "";
        errorMessage =
          genericErrorsList && genericErrorsList.length > 0
            ? tryFormatMessageWithJson(genericErrorsList[0].description)
            : "";
        enableRefreshPanel = err.response.data.enableRefreshPanelOption;
      }
    } else if (err.response.status === 409) {
      errorMessage = err.response.data.description;
      enableRefreshPanel = err.response.data.enableRefreshPanelOption;
      notifyError = err.response.data.endUserException;
      err.message = tryFormatMessageWithJson(errorMessage);
      err.enableRefreshPanelOption = enableRefreshPanel;
    } else if (err.response.status === 413) {
      const genericErrorsList = err.response.data;
      errorTitle = genericErrorsList && genericErrorsList.length > 0 ? genericErrorsList[0].error : "";
      errorMessage = genericErrorsList && genericErrorsList.length > 0 ? genericErrorsList[0].description : "";
      notifyError = false;
      enableRefreshPanel = err.response.data.enableRefreshPanelOption;
    } else if (err.response.status === 404) {
      const coServiceError = err.response.data.httpStatus;
      if (coServiceError) {
        errorTitle = tryFormatMessageWithJson(err.response.data.error);
        errorMessage = tryFormatMessageWithJson(err.response.data.description);
      } else {
        errorTitle = err.response.data.status;
        errorMessage = formatMessage("cof.axios.error.serviceNotAvailable");
      }
    } else if (err.response.status === 555) {
      notifyError = false;
      StandaloneModalManager.create({
        title: "iXBRL",
        message: formatMessage(err.response.data[0].description),
        type: err.response.data[0].error,
      });
    } else {
      errorTitle = err.response.status;
      errorMessage = formatMessage("cof.axios.error.message.general");
      if (err.response.data && typeof err.response.data === "string") {
        errorMessage = err.response.data;
      } else if (err.response.data && err.response.data.statusText) {
        errorMessage = err.response.data.statusText;
      }
    }
    if (notifyError) {
      NotificationManager.addNotification({
        title: errorTitle,
        message: errorMessage,
        key: API_CALL_NOTIFICATION,
        type: severity.toLowerCase() as "success" | "error" | "warning" | "info",
        closable: true,
        removeOnClose: true,
        isTemporary: false,
        enableReloadPanel: enableRefreshPanel,
      });
    }
    return Promise.reject(err);
  }
);

// @ts-ignore
/**
 * we use generic http client not axios instance
 * the url is self-signed and we get thg binary
 * content of the response
 */
export async function getPublicResourceAsBase64Binary(url: string): Promise<string> {
  const response = await axios.get(url, {
    responseType: "arraybuffer",
  });
  if (response.status === 200) {
    // eslint-disable-next-line no-undef
    return Buffer.from(response.data, "binary").toString("base64");
  } else {
    throw new Error("Error getting content: " + response.statusText + " (" + response.status + ")");
  }
}

type AuthErrorCauseCode = "NO_REFRESH_TOKEN" | "FAIL_TO_REFRESH_TOKEN" | "NO_TOKEN";
class AuthError extends Error {
  constructor(public cause: AuthErrorCauseCode, public code: string) {
    super(cause);
  }
}

export function mapErrorToMessage(e): { message: string; title: string } {
  let title = formatMessage("cof.axios.error.title.general");
  let message = formatMessage("cof.axios.error.message.general");
  if (e.code === "401" || e.response?.status === 401) {
    title = formatMessage("cof.axios.error.title.authorization");
    message = formatMessage("cof.axios.error.message.authorization.required");
  }
  if (e.message == "NO_REFRESH_TOKEN" || e.message == "FAIL_TO_REFRESH_TOKEN") {
    message = formatMessage("cof.axios.error.message.authorization.expired");
  }
  if (e.message == "NO_TOKEN") {
    message = formatMessage("cof.axios.error.message.authorization.required");
  }
  return { message, title };
}
